export type ServiceVariantAttributeValue = {
  id: number;
  label: string;
  /** Odoo attribute surcharge, e.g. 500 for "ازالة عازل حراري". */
  price_extra?: number | null;
};

export type ServiceVariantAttributeGroup = {
  label: string;
  values: ServiceVariantAttributeValue[];
};

export type ServiceVariant = {
  id: number;
  name: string;
  attributes: Record<string, number>;
  lst_price: number;
  lst_price_discount?: number | null;
  work_hours?: number | null;
  expected_duration?: number | null;
  warranty?: number | string | null;
  total_sales_count?: number | null;
  is_published?: boolean;
};

export type ServiceProductVariants = {
  id: number;
  name: string;
  categ_id?: string;
  description?: string | false;
  description_website?: string | false;
  feature_ids?: Array<{ id: number; name: string }>;
  image_1920?: unknown;
  price_from?: number;
  variant_count?: number;
  variant_attributes?: Record<string, ServiceVariantAttributeGroup>;
  variants?: ServiceVariant[];
};

export type ServiceVariantSelections = Record<string, number>;

/** Localized display text for the known service attributes returned by the API. */
export function getServiceAttributeLabel(key: string, label: string, language: string): string {
  const normalized = key.trim().toLowerCase();
  const arabic = language.startsWith("ar");
  if (["piece", "pieces"].includes(normalized)) return arabic ? "عدد القطع" : "Pieces";
  if (["size", "car_size", "car size"].includes(normalized)) return arabic ? "حجم السيارة" : "Car size";
  if (["layer", "layers"].includes(normalized)) return arabic ? "عدد الطبقات" : "Layers";
  return label;
}

export function getServiceAttributeValueLabel(key: string, label: string, language: string): string {
  const normalized = key.trim().toLowerCase();
  const value = label.trim().toLowerCase();
  const arabic = language.startsWith("ar");
  if (["piece", "pieces"].includes(normalized)) {
    const count = Number(value.match(/\d+/)?.[0]);
    if (count >= 1 && count <= 3) {
      return arabic ? ["", "قطعة واحدة", "قطعتان", "3 قطع"][count] : `${count} ${count === 1 ? "piece" : "pieces"}`;
    }
  }
  if (["size", "car_size", "car size"].includes(normalized)) {
    const sizes: Record<string, [string, string]> = {
      small: ["صغير", "Small"], medium: ["متوسط", "Medium"],
      large: ["كبير", "Large"], "x large": ["كبير جداً", "Extra Large"],
      "extra large": ["كبير جداً", "Extra Large"],
    };
    const size = sizes[value];
    if (size) return size[arabic ? 0 : 1];
  }
  if (["layer", "layers"].includes(normalized)) {
    const counts: Record<string, number> = {
      "one layer": 1, "two layers": 2, "three layers": 3,
      "four layers": 4, "five layers": 5,
    };
    const count = counts[value] ?? Number(value.match(/\d+/)?.[0]);
    if (count >= 1 && count <= 5) {
      return arabic
        ? ["", "طبقة واحدة", "طبقتان", "3 طبقات", "4 طبقات", "5 طبقات"][count]
        : `${count} ${count === 1 ? "layer" : "layers"}`;
    }
  }
  return label;
}

/**
 * Car size (Small / Medium / Large / X Large) is not the customer's choice: it
 * is a property of the vehicle on their account. The group is kept out of the
 * selector until the backend returns the size for the selected car; variants
 * still carry the attribute, so matching keeps whichever size the default
 * variant holds.
 */
export const CAR_SIZE_ATTRIBUTE_KEY = "size";

export function isCarSizeAttributeKey(attributeKey: string): boolean {
  return attributeKey.toLocaleLowerCase() === CAR_SIZE_ATTRIBUTE_KEY;
}

export function getVisibleVariantAttributes(
  variantAttributes?: Record<string, ServiceVariantAttributeGroup>
): Record<string, ServiceVariantAttributeGroup> {
  if (!variantAttributes || typeof variantAttributes !== "object") return {};

  return Object.fromEntries(
    Object.entries(variantAttributes).filter(
      ([attributeKey]) => !isCarSizeAttributeKey(attributeKey)
    )
  );
}

/**
 * Surcharge to advertise on an option, or 0 when there is nothing to show.
 * The variant's own `lst_price` already reflects the choice, so this is a hint
 * for the customer and must not be added to the displayed price.
 */
export function getAttributeValueExtra(
  value: ServiceVariantAttributeValue | null | undefined
): number {
  const extra = Number(value?.price_extra);
  return Number.isFinite(extra) && extra > 0 ? extra : 0;
}

export function getSelectableAttributeKeys(
  variantAttributes?: Record<string, ServiceVariantAttributeGroup>
): string[] {
  if (!variantAttributes || typeof variantAttributes !== "object") return [];
  return Object.keys(variantAttributes);
}

export function getVariantEffectivePrice(variant: ServiceVariant): number {
  const discount = Number(variant.lst_price_discount);
  const price = Number(variant.lst_price);
  if (Number.isFinite(discount) && discount > 0) return discount;
  if (Number.isFinite(price) && price > 0) return price;
  return Number.POSITIVE_INFINITY;
}

export function getCheapestVariant(
  variants: ServiceVariant[] | undefined
): ServiceVariant | null {
  if (!variants?.length) return null;
  return variants.reduce((cheapest, current) =>
    getVariantEffectivePrice(current) < getVariantEffectivePrice(cheapest)
      ? current
      : cheapest
  );
}

export function pickSelectionsFromVariant(
  variant: ServiceVariant | null | undefined,
  selectableKeys: string[]
): ServiceVariantSelections {
  if (!variant?.attributes) return {};
  if (selectableKeys.length === 0) return { ...variant.attributes };

  const selections: ServiceVariantSelections = {};
  for (const key of selectableKeys) {
    const value = variant.attributes[key];
    if (value != null) selections[key] = value;
  }
  return selections;
}

export function extractServiceFromProductVariantsPayload(
  payload: unknown,
  serviceId?: number
): Record<string, unknown> | null {
  const rows = Array.isArray(payload)
    ? payload
    : payload && typeof payload === "object" && Array.isArray((payload as any).data)
      ? (payload as any).data
      : payload && typeof payload === "object" && (payload as any).data
        ? [(payload as any).data]
        : payload && typeof payload === "object" && Array.isArray((payload as any).result)
          ? (payload as any).result
          : payload && typeof payload === "object" &&
              (payload as any).result &&
              typeof (payload as any).result === "object"
            ? [(payload as any).result]
            : [];

  if (rows.length === 0) return null;

  const match =
    serviceId != null
      ? rows.find((row: any) => Number(row?.id) === Number(serviceId))
      : rows[0];

  const item = match ?? rows[0];
  return item && typeof item === "object" ? (item as Record<string, unknown>) : null;
}

export function parseServiceProductVariantsPayload(
  payload: unknown,
  serviceId?: number
): ServiceProductVariants | null {
  const item = extractServiceFromProductVariantsPayload(payload, serviceId);
  if (!item) return null;

  const variants = Array.isArray(item.variants)
    ? item.variants.filter(
        (variant: ServiceVariant) => variant?.is_published !== false
      )
    : [];

  const variantAttributes =
    item.variant_attributes && typeof item.variant_attributes === "object"
      ? item.variant_attributes
      : {};

  const attributeKeys = Object.keys(variantAttributes);
  if (variants.length === 0) {
    return null;
  }

  if (attributeKeys.length === 0 && variants.length === 1) {
    return null;
  }

  if (attributeKeys.length === 0) {
    return null;
  }

  return {
    ...item,
    variant_attributes: variantAttributes,
    variants,
  } as ServiceProductVariants;
}

export function getDefaultVariantSelections(
  item: ServiceProductVariants
): ServiceVariantSelections {
  const selectableKeys = getSelectableAttributeKeys(item.variant_attributes);
  const cheapest = getCheapestVariant(item.variants);
  return pickSelectionsFromVariant(cheapest, selectableKeys);
}

export function findVariantBySelections(
  variants: ServiceVariant[] | undefined,
  selections: ServiceVariantSelections,
  selectableKeys?: string[]
): ServiceVariant | null {
  if (!variants?.length) return null;

  const keys =
    selectableKeys && selectableKeys.length > 0
      ? selectableKeys.filter((key) => selections[key] != null)
      : Object.keys(selections);

  if (keys.length === 0) {
    return getCheapestVariant(variants);
  }

  const exact = variants.find((variant) =>
    keys.every((key) => variant.attributes?.[key] === selections[key])
  );
  if (exact) return exact;

  const partialMatches = variants.filter((variant) =>
    keys.every((key) => variant.attributes?.[key] === selections[key])
  );
  return getCheapestVariant(partialMatches) ?? getCheapestVariant(variants);
}

export function isVariantOptionAvailable(
  variants: ServiceVariant[],
  selectableKeys: string[],
  currentSelections: ServiceVariantSelections,
  attributeKey: string,
  valueId: number
): boolean {
  const probe: ServiceVariantSelections = {};
  for (const key of selectableKeys) {
    if (key === attributeKey) {
      probe[key] = valueId;
    } else if (currentSelections[key] != null) {
      probe[key] = currentSelections[key];
    }
  }

  return variants.some((variant) =>
    Object.keys(probe).every((key) => variant.attributes?.[key] === probe[key])
  );
}

export function resolveSelectionsAfterChange(
  variants: ServiceVariant[],
  current: ServiceVariantSelections,
  attributeKey: string,
  valueId: number,
  selectableKeys: string[]
): ServiceVariantSelections {
  const base: ServiceVariantSelections = {};
  for (const key of selectableKeys) {
    if (current[key] != null) base[key] = current[key];
  }
  base[attributeKey] = valueId;

  const match = findVariantBySelections(variants, base, selectableKeys);
  if (match) return pickSelectionsFromVariant(match, selectableKeys);

  const partial = variants.find(
    (variant) => variant.attributes?.[attributeKey] === valueId
  );
  if (partial) return pickSelectionsFromVariant(partial, selectableKeys);

  return base;
}

export function buildCartServiceFromVariant(
  base: Record<string, unknown>,
  variant: ServiceVariant
) {
  const workHours =
    variant.work_hours ??
    variant.expected_duration ??
    (base as any).work_hours ??
    (base as any).expected_duration;

  return {
    ...base,
    ...variant,
    id: variant.id,
    parent_service_id: base.id,
    name: variant.name || base.name,
    lst_price: variant.lst_price,
    price_from: variant.lst_price,
    lst_price_discount: variant.lst_price_discount ?? variant.lst_price,
    work_hours: workHours,
    expected_duration: workHours,
    warranty: variant.warranty ?? (base as any).warranty,
  };
}
